import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useBranch } from '@/features/branch/branch-context';
import { api } from '@/lib/api';

export type TeleType = 'follow_up' | 'first_visit';
export type TeleStatus = 'booked' | 'in_progress' | 'closed' | 'converted';

export interface TeleconsultConfig {
  approved: boolean;
  approvedAt: string | null;
  approvedBy: string | null;
  source: string;
  videoProvider: string | null;
  checklist: { key: string; title: string; detail: string }[];
}
export interface Teleconsult {
  id: string;
  startsAt: string;
  patient: { id: string; fullName: string };
  doctor: { id: string; fullName: string };
  mine: boolean;
  type: TeleType;
  feePaidSen: number;
  status: TeleStatus;
  identityConfirmedAt: string | null;
  note: string | null;
  closedAt: string | null;
}

export function useTeleconsults(date: string) {
  const { selected } = useBranch();
  return useQuery({
    queryKey: ['teleconsults', selected?.id ?? 'all', date],
    queryFn: ({ signal }) =>
      api
        .get<{ date: string; config: TeleconsultConfig; rows: Teleconsult[] }>(
          '/teleconsults',
          {
            signal,
            query: { date },
          },
        )
        .then((r) => r.data),
  });
}

export function useTeleAction<V, R = unknown>(run: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['teleconsults'] }),
  });
}

export const teleApi = {
  create: (v: {
    patientId: string;
    startsAt: string;
    type: TeleType;
    feePaidSen: number;
  }) => api.post('/teleconsults', v),
  start: (id: string) =>
    api.post(`/teleconsults/${id}/start`, { identityConfirmed: true }),
  close: ({
    id,
    ...v
  }: {
    id: string;
    notes: string;
    firstVisitReason?: string;
  }) => api.post(`/teleconsults/${id}/close`, v),
  convert: ({ id, reason }: { id: string; reason: string }) =>
    api.post(`/teleconsults/${id}/convert`, { reason }),
  approve: (v: { note: string }) => api.post('/teleconsults/config/approve', v),
};
